/** Driver owns external DOM/listeners. Eligibility and section facts come exclusively from the server. */
import { useEffect, useRef } from 'react'
import type { Driver } from 'driver.js'
import type { StudyBuilderEvaluationGuidance, ShopifyStudyBuilderEvaluationResponse } from '@testfeed/shopify-contracts'

export function useStudyBuilderGuidance(
  guide: StudyBuilderEvaluationGuidance | null,
  sections: ShopifyStudyBuilderEvaluationResponse['sections'],
) {
  const root = useRef<HTMLDivElement>(null)
  const active = useRef<Driver | null>(null)
  const generation = useRef(0)
  const autoStarted = useRef(false)
  // A changed server projection, disabled guide or unmount invalidates pending imports and active overlays.
  useEffect(
    () => () => {
      generation.current++
      active.current?.destroy()
      active.current = null
    },
    [guide?.id, guide?.sectionIds, sections],
  )

  const open = async (signal?: AbortSignal) => {
    if (!guide || !root.current) return
    const opening = ++generation.current
    const { showStudyBuilderGuidance } = await import('../../components/study-builder/studyBuilderGuidanceDisplay')
    signal?.throwIfAborted()
    if (generation.current !== opening || !root.current) throw new DOMException('Guide closed', 'AbortError')
    active.current?.destroy()
    active.current = showStudyBuilderGuidance(root.current, guide, sections)
  }

  // Server-owned first-time tour: the answer carries `autoStart` true and the targeted sections are on screen. It opens
  // by itself once per visit (the ref guards re-runs as later evaluates change the projection); the merchant can close it
  // at any step and replay it from the "…" menu. A failure to open is swallowed — the tour is optional help, never a gate.
  useEffect(() => {
    if (autoStarted.current || !guide?.autoStart || !guide.available || !root.current) return
    autoStarted.current = true
    void open().catch(() => {})
  }, [guide, sections])

  return { root, open }
}
